import { useMemo, useState } from 'react'
import { ArrowUpRightIcon, CircleMinusIcon, IdCardIcon, PlusIcon, RotateCcwIcon, TagIcon } from 'lucide-react'
import { Box, Link, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Input, Modal } from '@repo/ui'
import { SelectField } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { Term } from 'components/feedback/Term'
import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useAddCompetitor,
  useProposeCompetitors,
  useSetCompetitorKind,
  useSetCompetitorStatus,
} from 'hooks/api'
import type { SeoCompetitor, SeoCompetitorKind, SeoReviewStatus, SeoStatus } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, shortDate } from '../shared'
import { useCompetitorFeedActions } from '../../../CompetitorFeeds'
import { CompetitorProfileDrawer } from './CompetitorProfileDrawer'
import { competitorEvidenceLines, KIND_HELP, KIND_LABEL, REVIEW_LABEL, REVIEW_ORDER } from './seoReview'

const KINDS: SeoCompetitorKind[] = ['business', 'search', 'adjacent']
const DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/
const ADMIN_ONLY = 'Only platform admins can change the competitor set.'

/** Lowercase host, scheme / path / www stripped — the same rule the API applies. */
const cleanDomain = (raw: string) =>
  raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[/?#:].*$/, '')
    .replace(/^www\./, '')

const SOURCE_LABEL: Record<string, string> = {
  seed: 'Seeded',
  manual: 'Added by hand',
  serp: 'Search results',
  overlap: 'Keyword overlap',
}

/**
 * Add a competitor by hand. Approved on save: a person adding it IS the review. The kind
 * decides where it shows: business + search are matrix columns by default, adjacent only
 * when the Rankings toggle is on.
 */
const AddCompetitorModal = ({
  open,
  onClose,
  existing,
}: {
  open: boolean
  onClose: () => void
  existing: SeoCompetitor[]
}) => {
  const add = useAddCompetitor()
  const { open: notify } = useSnackbar()
  const [domain, setDomain] = useState('')
  const [kind, setKind] = useState<SeoCompetitorKind>('business')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setDomain('')
    setKind('business')
    setNote('')
    setError(null)
    onClose()
  }
  const clean = cleanDomain(domain)
  const already = existing.find((c) => c.domain === clean)
  const valid = DOMAIN_RE.test(clean)

  const submit = () => {
    if (!valid) {
      setError('Enter a domain such as example.com.')
      return
    }
    add.mutate(
      { domain: clean, kind, note: note.trim() || null },
      {
        onSuccess: (row) => {
          notify({ type: 'success', text: `Added ${row.domain} as a ${KIND_LABEL[row.kind].toLowerCase()} competitor.` })
          close()
        },
        onError: (e) => setError((e as Error).message),
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add competitor"
      description="Approved as soon as you add it. Business and search competitors become Rankings columns, are mined by the gap finder and are checked in AI answers. Adjacent ones show in Rankings only when you turn them on."
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!clean} loading={add.isPending}>
            Add competitor
          </Button>
        </>
      }
    >
      {error ? (
        <Box sx={{ mb: 3 }}>
          <Notice severity="error">{error}</Notice>
        </Box>
      ) : null}
      <Stack spacing={4}>
        <Input
          label="Domain"
          placeholder="example.com"
          value={domain}
          onChange={(v: string) => {
            setDomain(v)
            if (error) setError(null)
          }}
          error={Boolean(error)}
          helperText={
            already
              ? `${already.domain} is already in the list (${REVIEW_LABEL[already.status].toLowerCase()}). Adding it approves it${
                  already.kind !== kind ? ` and makes it ${KIND_LABEL[kind].toLowerCase()}` : ''
                }.`
              : clean && clean !== domain.trim()
              ? `Saved as ${clean}.`
              : undefined
          }
          disabled={add.isPending}
        />
        <SelectField
          label="Kind"
          value={kind}
          onChange={(v) => setKind(v as SeoCompetitorKind)}
          options={KINDS.map((k) => ({ value: k, label: KIND_LABEL[k], description: KIND_HELP[k] }))}
          disabled={add.isPending}
        />
        <Input
          label="Note"
          placeholder="Why it is a competitor (optional)"
          value={note}
          onChange={(v: string) => setNote(v)}
          multiline
          minRows={2}
          disabled={add.isPending}
        />
      </Stack>
    </Modal>
  )
}

/**
 * VRG-643 follow-up — the competitor set as a review list. Jobs propose (from search
 * results and from the keyword-overlap endpoint), a person approves or rejects. Only
 * approved competitors are Rankings columns and gap-finder targets.
 */
export const SeoCompetitorsCard = ({
  status,
  profileDomain: controlledProfile,
  onProfileChange,
}: {
  status: SeoStatus
  /** VRG-744 — Competitor research drives the open profile from `?competitor=`, so it is linkable. */
  profileDomain?: string | null
  onProfileChange?: (domain: string | null) => void
}) => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const counts = status.competitorCounts
  const [filter, setFilter] = useState<SeoReviewStatus>(counts.proposed > 0 ? 'proposed' : 'approved')
  const [addOpen, setAddOpen] = useState(false)
  const [localProfile, setLocalProfile] = useState<string | null>(null)
  const profileDomain = controlledProfile !== undefined ? controlledProfile : localProfile
  const setProfileDomain = onProfileChange ?? setLocalProfile
  const profile = status.competitors.find((c) => c.domain === profileDomain) ?? null
  const setStatus = useSetCompetitorStatus()
  const setKind = useSetCompetitorKind()
  const propose = useProposeCompetitors()
  const { open: notify } = useSnackbar()
  // VRG-741 — a competitor's blog/news feeds (found automatically; Re-scan / Add by hand). One
  // reusable piece so it moves with the competitor profile.
  const feeds = useCompetitorFeedActions()

  // Approved | Proposed | Rejected, left to right (REVIEW_ORDER). The OPEN filter is still
  // Proposed while anything waits for review — the order is where a status sits, not which
  // one a reviewer lands on.
  const filters: readonly ViewToggleOption<SeoReviewStatus>[] = REVIEW_ORDER.map((s) => ({
    value: s,
    label: `${REVIEW_LABEL[s]} (${counts[s]})`,
  }))
  const rows = useMemo(
    () => status.competitors.filter((c) => c.status === filter),
    [status.competitors, filter],
  )
  const rules = status.competitorRules

  const act = (c: SeoCompetitor, next: 'approved' | 'dismissed') =>
    setStatus.mutate(
      { domain: c.domain, status: next },
      {
        onSuccess: () =>
          notify({
            type: 'success',
            text:
              next === 'approved'
                ? `Approved ${c.domain}.`
                : c.status === 'approved'
                ? `Removed ${c.domain} from competitors.`
                : `Rejected ${c.domain}.`,
          }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )

  const approvedMenu = (c: SeoCompetitor): ActionDropdownItem[] => [
    {
      label: 'View profile',
      icon: <IdCardIcon size={18} strokeWidth={1} />,
      onClick: () => setProfileDomain(c.domain),
    },
    ...(c.status === 'approved' ? feeds.menuItems(c) : []),
    ...KINDS.filter((k) => k !== c.kind).map(
      (k): ActionDropdownItem => ({
        label: `Make ${KIND_LABEL[k].toLowerCase()}`,
        icon: <TagIcon size={18} strokeWidth={1} />,
        divider: k === KINDS.filter((x) => x !== c.kind)[0],
        disabled: !isAdmin || setKind.isPending,
        tooltip: !isAdmin ? ADMIN_ONLY : undefined,
        onClick: () =>
          setKind.mutate(
            { domain: c.domain, kind: k },
            {
              onSuccess: () => notify({ type: 'success', text: `${c.domain} is now ${KIND_LABEL[k].toLowerCase()}.` }),
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          ),
      }),
    ),
    {
      label: 'Remove from competitors',
      icon: <CircleMinusIcon size={18} strokeWidth={1} />,
      divider: true,
      disabled: !isAdmin || setStatus.isPending,
      tooltip: !isAdmin ? ADMIN_ONLY : undefined,
      onClick: () => act(c, 'dismissed'),
    },
  ]

  const reviewButtons = (c: SeoCompetitor) => (
    <Stack direction="row" spacing={3} justifyContent="flex-end">
      <Button
        variant="secondary"
        disabled={!isAdmin || setStatus.isPending}
        disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
        onClick={() => act(c, 'dismissed')}
      >
        Reject
      </Button>
      <Button
        variant="primary"
        disabled={!isAdmin || setStatus.isPending}
        disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
        onClick={() => act(c, 'approved')}
      >
        Approve
      </Button>
    </Stack>
  )

  const groups: Array<{ kind: SeoCompetitorKind; rows: SeoCompetitor[] }> =
    filter === 'approved'
      ? KINDS.map((k) => ({ kind: k, rows: rows.filter((c) => c.kind === k) }))
      : [{ kind: 'business', rows }]

  return (
    <Card
      title="Competitors"
      action={
        <Button
          variant="primary"
          startIcon={<PlusIcon size={18} strokeWidth={1.5} />}
          disabled={!isAdmin}
          disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
          onClick={() => setAddOpen(true)}
        >
          Add competitor
        </Button>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
        >
          <ViewToggle options={filters} value={filter} onChange={setFilter} ariaLabel="Competitor status" />
          <Button
            variant="secondary"
            disabled={!isAdmin || propose.isPending}
            disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
            loading={propose.isPending}
            onClick={() =>
              propose.mutate(undefined, {
                onSuccess: (r) =>
                  notify({
                    type: 'success',
                    text: r.proposed.length + r.reproposed.length
                      ? `Proposed ${[...r.proposed, ...r.reproposed].join(', ')}.`
                      : 'Nothing new to propose from the stored search results.',
                  }),
                onError: (e) => notify({ type: 'error', text: (e as Error).message }),
              })
            }
          >
            Check search results
          </Button>
        </Stack>
        <SectionNote>
          Proposed automatically when a site is in the top {rules.serpTopN} for at least{' '}
          {rules.serpMinTerms} tracked terms in one market across each term&rsquo;s last{' '}
          {rules.serpChecks} checks, or shares at least {rules.overlapMinCommon} keywords with a
          competitor you pull in the gap finder. Checking stored results costs nothing. Generic
          platforms ({rules.exclusions.slice(1, 6).join(', ')} and {rules.exclusions.length - 6} more)
          are never proposed. A rejected site comes back only if its evidence doubles.
        </SectionNote>

        {groups.map((g) => (
          <Box key={`${filter}-${g.kind}`}>
            {filter === 'approved' ? (
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {KIND_LABEL[g.kind]}{' '}
                <Typography component="span" variant="body3" color="text.secondary">
                  · {g.rows.length} · {KIND_HELP[g.kind]}
                </Typography>
              </Typography>
            ) : null}
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Name</HeadCell>
                  <HeadCell>Domain</HeadCell>
                  {filter !== 'approved' ? <HeadCell>Kind</HeadCell> : null}
                  {filter === 'approved' ? <HeadCell align="right">Risk</HeadCell> : null}
                  {filter === 'approved' ? <HeadCell align="right">
                      <Term id="competitor-feed">Feeds</Term>
                    </HeadCell> : null}
                  <HeadCell>{filter === 'proposed' ? 'Evidence' : filter === 'approved' ? 'Segment' : 'Notes'}</HeadCell>
                  <HeadCell>{filter === 'proposed' ? 'Found by' : 'Source'}</HeadCell>
                  <HeadCell>{filter === 'approved' ? 'Since' : filter === 'proposed' ? 'Proposed' : 'Rejected'}</HeadCell>
                  <HeadCell align="right"> </HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {g.rows.length === 0 ? (
                  <EmptyRow
                    colSpan={8}
                    text={
                      filter === 'proposed'
                        ? 'Nothing waiting for review.'
                        : filter === 'dismissed'
                        ? 'Nothing rejected.'
                        : `No ${KIND_LABEL[g.kind].toLowerCase()} competitors.`
                    }
                  />
                ) : (
                  g.rows.map((c) => {
                    const evidence = competitorEvidenceLines(c.evidence)
                    return (
                      <TableRow key={c.domain}>
                        {/* Name and domain are separate columns (Spencer, 2026-10-01). The
                            name opens the profile, as the domain did; the domain is the site,
                            in a new tab, the same treatment the profile drawer gives it. A row
                            with no name yet shows its domain there rather than a dash, so the
                            profile is always one click away. */}
                        <Cell strong>
                          <InfoTooltip text="Open profile">
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              color="inherit"
                              onClick={() => setProfileDomain(c.domain)}
                              sx={{ font: 'inherit', fontWeight: 600, textAlign: 'left' }}
                            >
                              {c.name?.trim() || c.domain}
                            </Link>
                          </InfoTooltip>
                        </Cell>
                        <Cell>
                          <Link
                            href={`https://${c.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="inherit"
                            aria-label={`${c.domain} (opens in a new tab)`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            {c.domain}
                            <ArrowUpRightIcon size={14} strokeWidth={1} style={{ flexShrink: 0, opacity: 0.6 }} />
                          </Link>
                        </Cell>
                        {filter !== 'approved' ? <Cell>{KIND_LABEL[c.kind]}</Cell> : null}
                        {filter === 'approved' ? (
                          <Cell align="right">{c.profile.riskRank ?? 'TBV'}</Cell>
                        ) : null}
                        {filter === 'approved' ? (
                          <Cell align="right">
                            {/* VRG-741 — feed count, or "No feed found · date" with an Add. */}
                            {feeds.addFeedLink(c)}
                          </Cell>
                        ) : null}
                        <Cell>
                          <Box sx={{ whiteSpace: 'normal', minWidth: 260, maxWidth: 520, lineHeight: 1.5 }}>
                            {filter === 'proposed'
                              ? evidence.length
                                ? evidence.map((l) => <div key={l}>{l}</div>)
                                : c.notes ?? '—'
                              : filter === 'approved'
                              ? (c.profile.segment ?? c.notes ?? '—').replace(/\*\*/g, '')
                              : c.notes ?? '—'}
                          </Box>
                        </Cell>
                        <Cell>{SOURCE_LABEL[c.source] ?? c.source}</Cell>
                        <Cell>{c.statusChangedAt ? shortDate(c.statusChangedAt) : '—'}</Cell>
                        <Cell align="right">
                          {filter === 'proposed' ? (
                            reviewButtons(c)
                          ) : filter === 'approved' ? (
                            <ActionDropdown items={approvedMenu(c)} />
                          ) : (
                            <ActionDropdown
                              items={[
                                {
                                  label: 'Restore to competitors',
                                  icon: <RotateCcwIcon size={18} strokeWidth={1} />,
                                  disabled: !isAdmin || setStatus.isPending,
                                  tooltip: !isAdmin ? ADMIN_ONLY : undefined,
                                  onClick: () => act(c, 'approved'),
                                },
                              ]}
                            />
                          )}
                        </Cell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </StatTable>
          </Box>
        ))}
      </Stack>
      <AddCompetitorModal open={addOpen} onClose={() => setAddOpen(false)} existing={status.competitors} />
      <CompetitorProfileDrawer competitor={profile} onClose={() => setProfileDomain(null)} />
      {feeds.modal}
    </Card>
  )
}
